import type { TeamRound } from '@/types/dde/teamRound.types'

import type { OddsFilters } from './types'

export const createFiltersKey = (tournamentId: string, filters: OddsFilters): string => {
  return JSON.stringify({
    tournamentId,
    marketType: filters.marketType?.value,
    players: [...filters.players].sort(),
    startDateTime: filters.startDateTime?.toISOString(),
    endDateTime: filters.endDateTime?.toISOString()
  })
}

export const getTeamsByPlayers = (
  entryList: Array<{ teamNo: number; players: Array<{ playerId: number }> }>,
  selectedPlayerIds: number[]
): number[] => {
  return entryList
    .filter((entry) => {
      return entry.players.some((player) => selectedPlayerIds.includes(player.playerId))
    })
    .map((entry) => entry.teamNo)
}

export const getTournamentRounds = (
  tournament: { rounds?: Array<{ roundNo: number }> } | null
): number[] => {
  return tournament?.rounds?.map((r) => r.roundNo) || [1, 2, 3, 4]
}

export const createConnectionKey = (teamNo: number, roundNo: number): string => {
  return `${teamNo}-${roundNo}`
}

export const createWebSocketUrl = (
  tournamentId: string,
  roundNo: number,
  teamNo: number
): string => {
  return `${import.meta.env.VITE_DDE_MSFP_WEBSOCKET}/golf/tournaments/${tournamentId}/round/${roundNo}/team/${teamNo}`
}

export const isAuthorizationMessage = (data: unknown): boolean => {
  return (
    typeof data === 'object' &&
    data !== null &&
    'message' in data &&
    (data as { message?: string }).message === 'Authorisation accepted'
  )
}

export const isTeamRoundData = (data: unknown): boolean => {
  return (
    typeof data === 'object' &&
    data !== null &&
    ('eventElementType' in data || 'roundNo' in data || 'holeNo' in data)
  )
}

export const matchesPlayerFilter = (action: TeamRound, selectedPlayerIds: number[]): boolean => {
  if (selectedPlayerIds.length === 0) return true
  return selectedPlayerIds.includes(action.playerId)
}

export const matchesTimeFilter = (
  action: TeamRound,
  messageTime: Date,
  startDateTime: Date | null,
  endDateTime: Date | null
): boolean => {
  if (!startDateTime || !endDateTime) return true

  const actionTimestamp =
    'timestamp' in action && action.timestamp ? new Date(action.timestamp as string) : messageTime

  const startTime = startDateTime.getTime()
  const endTime = endDateTime.getTime()
  const actionTime = actionTimestamp.getTime()

  return actionTime >= startTime && actionTime <= endTime
}

export const addTimestampToAction = (
  action: TeamRound,
  messageTime: Date
): TeamRound & { timestamp: string } => {
  return {
    ...action,
    timestamp: ('timestamp' in action && action.timestamp
      ? action.timestamp
      : messageTime.toISOString()) as string
  }
}

export const findExistingActionIndex = (actions: TeamRound[], action: TeamRound): number => {
  return actions.findIndex(
    (existing) =>
      existing.eventElementType === action.eventElementType &&
      existing.roundNo === action.roundNo &&
      existing.holeNo === action.holeNo &&
      existing.strokeNo === action.strokeNo &&
      existing.playerId === action.playerId
  )
}

export const areAllConnectionsReceived = (
  expectedConnections: Set<string>,
  receivedConnections: Set<string>
): boolean => {
  return (
    expectedConnections.size > 0 &&
    Array.from(expectedConnections).every((key) => receivedConnections.has(key))
  )
}
